import { Button, message } from 'antd';
import dayjs from '@/utils/dayjsConfig';
import { ProTable, ProColumns } from '@ant-design/pro-components';
import type { ProFormInstance } from '@ant-design/pro-components';
import { useState, useTransition, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterParams } from '@/facilities/model/user';
import { getList } from '@api';
import { useMenuItem, useUtilsMemo } from '@/hooks';
import { ReportTitle, CollapseFilters } from '@/pages/report/components';
import { parseQueryString, formaReportTable, parseMonth } from '@/utils/toolt';
import { isNaN } from 'lodash-es';
import { useReactToPrint } from 'react-to-print';
import { ExportFile, FilterComponet, SvgIcon } from '@/components';
import {
  TABLEPAI,
  DIVISION,
  DIVISION_LIFTCOMPANY,
  LIFTCOMPANY,
  MONTH_PARMS,
} from '@/enums/pageEnum';

export type Key = React.Key;
export default function MPTRCOM() {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const [data, setData] = useState<object[]>([]);
  const divClass = 'border-b-[1px] border-[#E8E9ED] dark:border-[#ffffff21] mb-4';
  const { previousMonth, monthObj } = useUtilsMemo();
  const [titleMonth, setTitleMonth] = useState<Date>(previousMonth);
  const printRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<ProFormInstance>(null);
  const [reportType, setReportType] = useState(DIVISION);

  const getPathParams = useCallback(() => parseMonth(parseQueryString()), []);

  const exportUrl = useMemo(() => {
    switch (reportType) {
      case DIVISION:
        return '/api/reports/tcmr-division/export-excel';
      case LIFTCOMPANY:
        return '/api/reports/tcmr-lift-company/export-excel';
      case DIVISION_LIFTCOMPANY:
        return '/api/reports/tcmr-dlc/export-excel';
      default:
        break;
    }
  }, [reportType]);

  const getTableLists = useCallback(
    (prev?: any) => {
      startTransition(async () => {
        try {
          const parms = getPathParams();
          const defaultMonth = dayjs(previousMonth).format(MONTH_PARMS);
          let month = parms.month || defaultMonth;
          let reportBy = (parms.reportBy as keyof typeof TABLEPAI) || DIVISION;
          if (prev && prev?.isReset) {
            month = defaultMonth;
            reportBy = DIVISION;
            formRef?.current?.resetFields();
          }
          setReportType(reportBy);
          const res = await getList({
            url: TABLEPAI[reportBy],
            params: { month },
          });
          setData(res.data.body);
        } catch (error) {
          message.error(JSON.stringify(error));
        }
      });
    },
    [getPathParams, previousMonth],
  );

  const dataSource: any = useMemo(() => {
    if (!data.length) return [];
    if (reportType === DIVISION_LIFTCOMPANY) {
      return [...formaReportTable(data, 'buildingWard'), data[data.length - 1]];
    }
    return data;
  }, [data, reportType]);

  useMenuItem({ callBack: getTableLists });

  const totalScoreDom = (text: any, max: number, min: number) => {
    const scorUnmber = Number(text);
    if (isNaN(scorUnmber)) return text;
    if (text >= max) return <p className="green">{text}</p>;
    if (text >= min && text < max) return <p className="amber">{text}</p>;
    return <p className="red">{text}</p>;
  };

  const getTableColumns: any[] = useMemo(() => {
    let columns: ProColumns[] = [];
    const oncellParms = {
      align: 'center',
      onCell: (record: any) => (record.groupLift ? { colSpan: 0 } : {}),
    };
    if (reportType !== DIVISION_LIFTCOMPANY) {
      columns = [
        {
          title: reportType === DIVISION ? t('ward') : 'SNO',
          dataIndex: 'buildingWard',
          align: 'center',
          render: (text: any, record, index) => (
            <>
              {record.tcmrCount?.summary
                ? t('summary')
                : reportType === DIVISION
                  ? text
                  : index + 1}
            </>
          ),
          onCell: (record) =>
            record.tcmrCount?.summary ? { colSpan: 2 } : { colSpan: 1 },
        },
        {
          title: reportType === DIVISION ? t('descriptions') : t('liftCompanies'),
          dataIndex: reportType === DIVISION ? 'buildingWardFullName' : 'liftCompany',
          align: 'center',
          onCell: (record) =>
            record.tcmrCount?.summary ? { colSpan: 0 } : { colSpan: 1 },
        },
      ];
    } else {
      columns = [
        {
          title: <p className="text-center">{t('wardDes')}</p>,
          dataIndex: 'liftCompany',
          align: 'left',
          onCell: (record) => (record.groupLift ? { colSpan: 9 } : {}),
          render: (text: any, record) => {
            if (!record.groupLift)
              return <>{record.tcmrCount?.summary ? t('summary') : text}</>;
            return (
              <div className="">
                <span className="font-medium">Ward：</span>
                <span>{record.buildingWardFullName || record.buildingWard}</span>
              </div>
            );
          },
        },
      ];
    }
    return [
      ...columns,
      {
        title: t('liftNumber'),
        dataIndex: ['tcmrCount', 'liftCnt'],
        render: (text: any) => <>{t(text)}</>,
        ...oncellParms,
      },
      {
        title: t('tmsFault'),
        children: [
          {
            title: t('cases'),
            dataIndex: ['tcmrCount', 'cases'],
            ...oncellParms,
          },
          {
            title: t('rate'),
            dataIndex: ['tcmrCount', 'faultRatePer10Units'],
            ...oncellParms,
          },
          {
            title: t('score'),
            dataIndex: ['tcmrCount', 'frScore'],
            className: 'colourBandsTd',
            render: (text: any) => totalScoreDom(text, 4, 2),
            ...oncellParms,
          },
        ],
      },
      {
        title: t('liftDownTime'),
        children: [
          {
            title: t('hours'),
            dataIndex: ['tcmrCount', 'hours'],
            ...oncellParms,
          },
          {
            title: t('avgLift'),
            dataIndex: ['tcmrCount', 'avgPerLift'],
            ...oncellParms,
          },
          {
            title: t('score'),
            dataIndex: ['tcmrCount', 'ldtScore'],
            ...oncellParms,
            className: 'colourBandsTd',
            render: (text: any) => totalScoreDom(text, 4, 2),
          },
        ],
      },
      {
        title: t('totalScore'),
        dataIndex: ['tcmrCount', 'totalScore'],
        className: 'colourBandsTd',
        ...oncellParms,
        render: (text: any) => totalScoreDom(text, 8, 4),
      },
    ];
  }, [t, reportType]);

  const tableTitle = useMemo(() => {
    const month = dayjs(titleMonth).format('MMM YYYY');
    return `TCMR Scoring for the Month of ${month}`;
  }, [titleMonth]);

  const filtersFileds: FilterParams[] = useMemo(() => {
    return [
      monthObj((date: Date) => {
        setTitleMonth(date);
      }),
      {
        component: 'groupTags',
        field: 'reportBy',
        label: t('reportBy'),
        options: [
          { label: 'Division', value: DIVISION },
          { label: 'Lift company', value: LIFTCOMPANY },
          { label: 'Division and lift company', value: DIVISION_LIFTCOMPANY },
        ],
        defaultValue: 'DIVISION',
        fieldProps: { divClass },
      },
    ];
  }, [t, divClass, monthObj]);

  const reactToPrintFn = useReactToPrint({ contentRef: printRef });

  const handleOnClick = useCallback(() => {
    reactToPrintFn();
  }, [reactToPrintFn]);

  const getPrintContent = useMemo(() => {
    const rightBorder = 'border-r-[1px] border-[#d3d3d3] dark:border-[#ffffff21] flex-1';
    return (
      <>
        <div className="text-center py-4 bg-[#f5f6f8] dark:bg-[#1d1d1d] border-[1px] border-b-[0px] border-[#d3d3d3] dark:border-[#ffffff21]">
          <h2 className="text-[20px] font-medium">{tableTitle}</h2>
        </div>
        <ProTable
          search={false}
          columns={getTableColumns}
          options={false}
          rowKey={'id'}
          loading={isPending}
          bordered
          rowHoverable
          scroll={{
            x: 'max-content',
          }}
          className="tcmrTableReport"
          dataSource={dataSource}
          columnEmptyText=" "
          pagination={false}
          rowSelection={false}
        />
        <div className="text-[14px] leading-9 mt-7">
          <h3 className="font-medium">
            Downtime calculated for Faults：3,5,6,7,12,15,16,19,22
          </h3>
          <h3 className="font-medium">Banding and Scoring Methdology</h3>
          <p>
            The total points (10 points) and point allocation for the banding remains
            unchanged:
          </p>
          <div className="border-[1px] border-[#d3d3d3] dark:border-[#ffffff21] text-center font-medium">
            <div className="flex justify-between border-b-[1px] border-[#d3d3d3] dark:border-[#ffffff21] bg-[#F5F6F8] dark:bg-[#ffffff21]">
              <div className={`${rightBorder} dark:bg-[#000000d9]`}>
                {t('colourBands')}
              </div>
              <div className={`${rightBorder} bg-[#03CE4D]`}>{t('green')}</div>
              <div className={`${rightBorder} bg-[#FFBF00]`}>{t('amber')}</div>
              <div className="flex-1 bg-[#FF5D60]">{t('red')}</div>
            </div>
            <div className="flex justify-between">
              <div className={`${rightBorder} font-normal`}>Points</div>
              <div className={rightBorder}>≥ 8 points</div>
              <div className={rightBorder}>{'4 to < 8 points'}</div>
              <div className="flex-1">{'< 4 points'}</div>
            </div>
          </div>
          <h3 className="font-medium mt-5">
            TMS Fault Rate and Lift Down Time Colour Bands
          </h3>
          <p>
            As both measures constitute equal weightage, TCs will be scored on a 5-point
            scale for both measures. The aggregate score will form the basis for the TC’s
            banding for Lift Performance.
          </p>
          <div className="border-[1px] border-[#d3d3d3] dark:border-[#ffffff21] text-center font-medium">
            <div className="flex justify-between border-b-[1px] border-[#d3d3d3] dark:border-[#ffffff21] bg-[#F5F6F8] dark:bg-[#ffffff21]">
              <div className={`${rightBorder} dark:bg-[#000000d9]`}>
                {t('colourBands')}
              </div>
              <div className={`${rightBorder} bg-[#03CE4D]`}>Green (≥ 8 points)</div>
              <div className={`${rightBorder} bg-[#FFBF00]`}>
                {'Amber (4 to < 8 points)'}
              </div>
              <div className="flex-1 bg-[#FF5D60]">{'Red (< 4 points)'}</div>
            </div>
            <div className="flex justify-between border-b-[1px] border-[#d3d3d3] dark:border-[#ffffff21]">
              <div className={`${rightBorder} font-normal`}>
                a{')'} TMS Fault Rate(per 10 lifts per month)
              </div>
              <div className={`${rightBorder} flex justify-between text-center`}>
                <div className={rightBorder}>{'5 (< 1)'}</div>
                <div className="flex-1">{'4 (1 to < 2)'}</div>
              </div>
              <div className={`${rightBorder} flex justify-between text-center`}>
                <div className={rightBorder}>{'3 (2 to < 3)'}</div>
                <div className="flex-1">{'2 (3 to < 4)'}</div>
              </div>
              <div className="flex-1">{'1 (≥ 4)'}</div>
            </div>
            <div className="flex justify-between">
              <div className={`${rightBorder} font-normal`}>
                b{')'} Lift Downtime(hours per lift per month)
              </div>
              <div className={`${rightBorder} flex justify-between text-center`}>
                <div className={rightBorder}>{'5 (< 0.8)'}</div>
                <div className="flex-1">{'4 (0.8 to <1)'}</div>
              </div>
              <div className={`${rightBorder} flex justify-between text-center`}>
                <div className={rightBorder}>{'3 (1 to <1.2)'}</div>
                <div className="flex-1">{'2 (1.2 to <1.4)'}</div>
              </div>
              <div className="flex-1">{'1 (≥ 1.4)'}</div>
            </div>
          </div>
          <p className="mt-2">
            A green band means that generally, the TC has less than 2 lift faults per 10
            lifts and less than 1 hour of downtime per lift, each month.
          </p>
        </div>
      </>
    );
  }, [dataSource, getTableColumns, t, isPending, tableTitle]);

  return (
    <div className="p-4">
      <ReportTitle title={t('tcmrReport')} />
      <CollapseFilters>
        <FilterComponet
          className="mb-[2px] filtersFromClass !py-0 !pl-0"
          layout="horizontal"
          submitNode={<>{t('preview')}</>}
          fieldsProp={filtersFileds}
          ref={formRef}
          submit={() => {
            getTableLists();
          }}
          reset={() => {
            getTableLists();
          }}
        />
      </CollapseFilters>
      <div className="mt-4 p-4 bg-[#fff] dark:bg-[#141414]">
        <div className="flex justify-start items-center pb-4 dark:border-[#ffffff21]">
          <ExportFile
            disabled={!data.length}
            onClick={() => {
              const { origin } = new URL(window.location.href);
              const downurl = new URLSearchParams({ ...getPathParams() });
              window.open(`${origin}${exportUrl}?${downurl?.toString()}`);
            }}
          />
          <Button
            color="primary"
            variant="outlined"
            disabled={!dataSource.length}
            onClick={handleOnClick}
            icon={<SvgIcon classa="print" />}
          >
            {t('print')}
          </Button>
        </div>
        {getPrintContent}
        <div ref={printRef} className="printContent">
          {getPrintContent}
        </div>
      </div>
    </div>
  );
}
